import { useAuth0 } from '@auth0/auth0-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { isFullBleedPath } from './fullBleedRoutes'

const navLinks = [{ to: '/home', label: 'Home' }]

const linkClassName = ({ isActive }: { isActive: boolean }) => {
  return [
    'rounded-lg px-3 py-2 text-sm font-medium block w-full text-left sm:block sm:w-auto',
    isActive
      ? 'bg-blue-50 text-blue-700 dark:bg-brand-600/20 dark:text-brand-400'
      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white',
  ].join(' ')
}

const Navigation: React.FC = () => {
  const { user, logout } = useAuth0()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const userMenuDesktopRef = useRef<HTMLDivElement>(null)
  const userMenuMobileRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  const closeMenu = useCallback(() => setIsMenuOpen(false), [])
  const closeUserMenu = useCallback(() => setIsUserMenuOpen(false), [])

  const initials = user?.name
    ? user.name
        .split(/\s+/)
        .map((s) => s[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user?.email
      ? user.email.slice(0, 2).toUpperCase()
      : '?'

  useEffect(() => {
    closeMenu()
  }, [location.pathname, closeMenu])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu()
        closeUserMenu()
      }
    }
    if (isMenuOpen || isUserMenuOpen) window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isMenuOpen, isUserMenuOpen, closeMenu, closeUserMenu])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current && !menuRef.current.contains(target)) closeMenu()
      const inDesktop = userMenuDesktopRef.current?.contains(target)
      const inMobile = userMenuMobileRef.current?.contains(target)
      if (!inDesktop && !inMobile) closeUserMenu()
    }
    if (isMenuOpen || isUserMenuOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen, isUserMenuOpen, closeMenu, closeUserMenu])

  // The case/order workspaces are full-bleed, so stretch the nav edge-to-edge
  // there too (logo flush left, Home/avatar flush right) to line up with the
  // panels below; other pages keep the centered bar. Shared with Layout via
  // fullBleedRoutes so the two can't drift (GEN-5698).
  const fullBleed = isFullBleedPath(location.pathname)

  return (
    <header ref={menuRef} className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div
        className={`flex flex-wrap items-center justify-between gap-4 px-4 py-3 ${fullBleed ? 'w-full' : 'mx-auto max-w-5xl'}`}
      >
        {/* Brand: full dark lockup (display only) */}
        <div className="flex shrink-0 items-center">
          <span className="sr-only">Inflection Medicine</span>
          <img src="/Inflection_Logo_Dark.svg" alt="" className="h-7 w-auto object-contain" aria-hidden />
        </div>

        {/* Desktop: nav links + user menu */}
        <nav className="hidden items-center gap-1 sm:flex" aria-label="Primary">
          {navLinks.map(({ to, label }) => (
            <NavLink key={to} to={to} className={linkClassName}>
              {label}
            </NavLink>
          ))}
          <div className="relative" ref={userMenuDesktopRef}>
            <button
              type="button"
              onClick={() => setIsUserMenuOpen((open) => !open)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              aria-expanded={isUserMenuOpen}
              aria-haspopup="true"
              aria-label="User menu"
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="h-full w-full rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span aria-hidden>{initials}</span>
              )}
            </button>
            {isUserMenuOpen && (
              <div
                role="menu"
                className="absolute top-full right-0 z-50 mt-2 min-w-40 rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                aria-label="User menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  onClick={() => {
                    closeUserMenu()
                    logout({ logoutParams: { returnTo: window.location.origin } })
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile: hamburger + user menu */}
        <div className="flex items-center gap-3 sm:hidden">
          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            <span className="sr-only">{isMenuOpen ? 'Close menu' : 'Open menu'}</span>
            <img src="/bars.svg" alt="" className="h-6 w-6" aria-hidden />
          </button>
          <div className="relative" ref={userMenuMobileRef}>
            <button
              type="button"
              onClick={() => setIsUserMenuOpen((open) => !open)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              aria-expanded={isUserMenuOpen}
              aria-haspopup="true"
              aria-label="User menu"
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="h-full w-full rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span aria-hidden>{initials}</span>
              )}
            </button>
            {isUserMenuOpen && (
              <div
                role="menu"
                className="absolute top-full right-0 z-50 mt-2 min-w-40 rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                aria-label="User menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  onClick={() => {
                    closeUserMenu()
                    logout({ logoutParams: { returnTo: window.location.origin } })
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <div
        id="mobile-menu"
        role="menu"
        aria-label="Primary navigation"
        className={`sm:hidden ${isMenuOpen ? 'block' : 'hidden'}`}
      >
        <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-700">
          <ul className="flex flex-col gap-1">
            {navLinks.map(({ to, label }) => (
              <li key={to}>
                <NavLink to={to} className={linkClassName} onClick={closeMenu}>
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </header>
  )
}

export default Navigation
